// Fix-once speakers: speaker panel, row popover, split, back-channels and the
// transcript around them (C-14).
//
// Namespace `meetings-speakers`; owner WP-6 (BUILD 1). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  speakersTitle: 'Speakers',
  speakerN: 'Speaker {n}',
  talkTime: '{minutes} min · {rows} rows',
  playSamples: 'Play samples',
  pickPerson: 'Who is this?',
  addGuest: 'Add a guest',
  notParticipant: 'Not a participant',
  mergeInto: 'Merge into…',
  unmerge: 'Separate again',
  whoSaidThis: 'Who said this?',
  onlyThisRow: 'Only this row',
  everyoneLabelled: 'Everyone labelled {label} ({rows} rows)',
  splitHere: 'Split here',
  whoSaidRest: 'Who said the rest?',
  notesRefreshing: 'Speakers updated · refreshing notes',
  notesUpToDate: 'Notes up to date',
  reaction: 'Reaction from {name}',
  speakersHint: 'Name each voice once. Every row of that voice follows, and the notes refresh.',
  stopSamples: 'Stop',
  attendees: 'Attendees',
  companyMembers: 'Company members',
  otherVoices: 'Other voices',
  guestName: 'Guest name',
  addGuestButton: 'Add',
  suggested: 'Suggested: {name}',
  mergedHere: '{name} merged here',
  mergeWithVoice: 'Same voice as {name}: merge',
  nameAnyway: 'Name it {name} anyway',
  sameSpeaker: 'Same speaker',
  split: 'Split',
  pickSplitWord: 'Click the first word the next speaker said.',
  cancel: 'Cancel',
  edit: 'Edit',
  save: 'Save',
  saving: 'Saving…',
  transcriptEyebrow: 'Cited source',
  transcriptTitle: 'Transcript',
  viewRepaired: 'Repaired',
  viewRaw: 'Raw',
  transcriptNote: 'Click a speaker name to fix who said it. The raw transcription is always kept.',
  unlabelled: 'Unknown speaker',
  gapMarker: 'Recording gap · {duration}',
  repairedCompany: 'Repaired from company context',
  repairedPoneva: 'Repaired by Poneva',
  changeFailed: 'The speaker change could not be saved.',
  editFailed: 'The transcript edit could not be saved.',
  splitNeedsWords: 'This row has no word timings, so it cannot be split.',
};

export type MeetingsSpeakersCopyKey = keyof typeof en;

const meetingsSpeakersCopy: NamespaceCatalog<MeetingsSpeakersCopyKey> = {
  bg: {
    speakersTitle: 'Говорещи',
    speakerN: 'Говорещ {n}',
    talkTime: '{minutes} мин · {rows} реда',
    playSamples: 'Пусни откъси',
    pickPerson: 'Кой е това?',
    addGuest: 'Добави гост',
    notParticipant: 'Не е участник',
    mergeInto: 'Обедини с…',
    unmerge: 'Раздели отново',
    whoSaidThis: 'Кой каза това?',
    onlyThisRow: 'Само този ред',
    everyoneLabelled: 'Всичко с етикет {label} ({rows} реда)',
    splitHere: 'Раздели тук',
    whoSaidRest: 'Кой каза останалото?',
    notesRefreshing: 'Говорещите са обновени · бележките се опресняват',
    notesUpToDate: 'Бележките са актуални',
    reaction: 'Реакция от {name}',
    speakersHint:
      'Назовете всеки глас веднъж. Всички редове с този глас се променят, а бележките се опресняват.',
    stopSamples: 'Спри',
    attendees: 'Участници',
    companyMembers: 'Членове на компанията',
    otherVoices: 'Други гласове',
    guestName: 'Име на госта',
    addGuestButton: 'Добави',
    suggested: 'Предложение: {name}',
    mergedHere: '{name} е обединен тук',
    mergeWithVoice: 'Същият глас като {name}: обедини',
    nameAnyway: 'Все пак го наречи {name}',
    sameSpeaker: 'Същият говорещ',
    split: 'Раздели',
    pickSplitWord: 'Щракнете върху първата дума, казана от следващия говорещ.',
    cancel: 'Отказ',
    edit: 'Редактирай',
    save: 'Запази',
    saving: 'Запазване…',
    transcriptEyebrow: 'Цитиран източник',
    transcriptTitle: 'Препис',
    viewRepaired: 'Коригиран',
    viewRaw: 'Суров',
    transcriptNote:
      'Щракнете върху името на говорещ, за да коригирате кой го е казал. Суровият препис винаги се запазва.',
    unlabelled: 'Неизвестен говорещ',
    gapMarker: 'Прекъсване в записа · {duration}',
    repairedCompany: 'Коригирано според контекста на компанията',
    repairedPoneva: 'Коригирано от Poneva',
    changeFailed: 'Промяната на говорещия не можа да бъде запазена.',
    editFailed: 'Редакцията на преписа не можа да бъде запазена.',
    splitNeedsWords: 'Този ред няма времена на думите, затова не може да бъде разделен.',
  },
  hr: {
    speakersTitle: 'Govornici',
    speakerN: 'Govornik {n}',
    talkTime: '{minutes} min · {rows} redaka',
    playSamples: 'Reproduciraj uzorke',
    pickPerson: 'Tko je ovo?',
    addGuest: 'Dodaj gosta',
    notParticipant: 'Nije sudionik',
    mergeInto: 'Spoji s…',
    unmerge: 'Ponovno razdvoji',
    whoSaidThis: 'Tko je ovo rekao?',
    onlyThisRow: 'Samo ovaj redak',
    everyoneLabelled: 'Sve s oznakom {label} ({rows} redaka)',
    splitHere: 'Podijeli ovdje',
    whoSaidRest: 'Tko je rekao ostatak?',
    notesRefreshing: 'Govornici ažurirani · bilješke se osvježavaju',
    notesUpToDate: 'Bilješke su ažurne',
    reaction: 'Reakcija: {name}',
    speakersHint:
      'Imenujte svaki glas jednom. Svi redci tog glasa slijede, a bilješke se osvježavaju.',
    stopSamples: 'Zaustavi',
    attendees: 'Sudionici',
    companyMembers: 'Članovi tvrtke',
    otherVoices: 'Drugi glasovi',
    guestName: 'Ime gosta',
    addGuestButton: 'Dodaj',
    suggested: 'Prijedlog: {name}',
    mergedHere: '{name} spojen ovdje',
    mergeWithVoice: 'Isti glas kao {name}: spoji',
    nameAnyway: 'Ipak nazovi {name}',
    sameSpeaker: 'Isti govornik',
    split: 'Podijeli',
    pickSplitWord: 'Kliknite prvu riječ koju je rekao sljedeći govornik.',
    cancel: 'Odustani',
    edit: 'Uredi',
    save: 'Spremi',
    saving: 'Spremanje…',
    transcriptEyebrow: 'Citirani izvor',
    transcriptTitle: 'Transkript',
    viewRepaired: 'Ispravljeno',
    viewRaw: 'Izvorno',
    transcriptNote:
      'Kliknite ime govornika da ispravite tko je to rekao. Izvorna transkripcija uvijek se čuva.',
    unlabelled: 'Nepoznati govornik',
    gapMarker: 'Praznina u snimci · {duration}',
    repairedCompany: 'Ispravljeno prema kontekstu tvrtke',
    repairedPoneva: 'Ispravila Poneva',
    changeFailed: 'Promjenu govornika nije bilo moguće spremiti.',
    editFailed: 'Izmjenu transkripta nije bilo moguće spremiti.',
    splitNeedsWords: 'Ovaj redak nema vremena riječi pa se ne može podijeliti.',
  },
  cs: {
    speakersTitle: 'Mluvčí',
    speakerN: 'Mluvčí {n}',
    talkTime: '{minutes} min · {rows} řádků',
    playSamples: 'Přehrát ukázky',
    pickPerson: 'Kdo to je?',
    addGuest: 'Přidat hosta',
    notParticipant: 'Není účastník',
    mergeInto: 'Sloučit s…',
    unmerge: 'Znovu oddělit',
    whoSaidThis: 'Kdo to řekl?',
    onlyThisRow: 'Jen tento řádek',
    everyoneLabelled: 'Vše označené {label} ({rows} řádků)',
    splitHere: 'Rozdělit zde',
    whoSaidRest: 'Kdo řekl zbytek?',
    notesRefreshing: 'Mluvčí aktualizováni · poznámky se obnovují',
    notesUpToDate: 'Poznámky jsou aktuální',
    reaction: 'Reakce: {name}',
    speakersHint:
      'Pojmenujte každý hlas jednou. Všechny řádky tohoto hlasu se změní a poznámky se obnoví.',
    stopSamples: 'Zastavit',
    attendees: 'Účastníci',
    companyMembers: 'Členové firmy',
    otherVoices: 'Jiné hlasy',
    guestName: 'Jméno hosta',
    addGuestButton: 'Přidat',
    suggested: 'Návrh: {name}',
    mergedHere: '{name} sloučen sem',
    mergeWithVoice: 'Stejný hlas jako {name}: sloučit',
    nameAnyway: 'Přesto pojmenovat {name}',
    sameSpeaker: 'Stejný mluvčí',
    split: 'Rozdělit',
    pickSplitWord: 'Klikněte na první slovo, které řekl další mluvčí.',
    cancel: 'Zrušit',
    edit: 'Upravit',
    save: 'Uložit',
    saving: 'Ukládání…',
    transcriptEyebrow: 'Citovaný zdroj',
    transcriptTitle: 'Přepis',
    viewRepaired: 'Opravený',
    viewRaw: 'Surový',
    transcriptNote:
      'Kliknutím na jméno mluvčího opravíte, kdo to řekl. Surový přepis se vždy uchová.',
    unlabelled: 'Neznámý mluvčí',
    gapMarker: 'Mezera v nahrávce · {duration}',
    repairedCompany: 'Opraveno podle kontextu firmy',
    repairedPoneva: 'Opraveno službou Poneva',
    changeFailed: 'Změnu mluvčího se nepodařilo uložit.',
    editFailed: 'Úpravu přepisu se nepodařilo uložit.',
    splitNeedsWords: 'Tento řádek nemá časy slov, proto ho nelze rozdělit.',
  },
  da: {
    speakersTitle: 'Talere',
    speakerN: 'Taler {n}',
    talkTime: '{minutes} min · {rows} linjer',
    playSamples: 'Afspil eksempler',
    pickPerson: 'Hvem er det?',
    addGuest: 'Tilføj en gæst',
    notParticipant: 'Ikke en deltager',
    mergeInto: 'Flet med…',
    unmerge: 'Adskil igen',
    whoSaidThis: 'Hvem sagde det?',
    onlyThisRow: 'Kun denne linje',
    everyoneLabelled: 'Alle markeret {label} ({rows} linjer)',
    splitHere: 'Del her',
    whoSaidRest: 'Hvem sagde resten?',
    notesRefreshing: 'Talere opdateret · noterne opdateres',
    notesUpToDate: 'Noterne er opdaterede',
    reaction: 'Reaktion fra {name}',
    speakersHint:
      'Navngiv hver stemme én gang. Alle linjer med den stemme følger med, og noterne opdateres.',
    stopSamples: 'Stands',
    attendees: 'Deltagere',
    companyMembers: 'Medlemmer af virksomheden',
    otherVoices: 'Andre stemmer',
    guestName: 'Gæstens navn',
    addGuestButton: 'Tilføj',
    suggested: 'Forslag: {name}',
    mergedHere: '{name} flettet ind her',
    mergeWithVoice: 'Samme stemme som {name}: flet',
    nameAnyway: 'Kald den {name} alligevel',
    sameSpeaker: 'Samme taler',
    split: 'Del',
    pickSplitWord: 'Klik på det første ord, den næste taler sagde.',
    cancel: 'Annuller',
    edit: 'Rediger',
    save: 'Gem',
    saving: 'Gemmer…',
    transcriptEyebrow: 'Citeret kilde',
    transcriptTitle: 'Transskription',
    viewRepaired: 'Rettet',
    viewRaw: 'Rå',
    transcriptNote:
      'Klik på et talernavn for at rette, hvem der sagde det. Den rå transskription gemmes altid.',
    unlabelled: 'Ukendt taler',
    gapMarker: 'Hul i optagelsen · {duration}',
    repairedCompany: 'Rettet ud fra virksomhedens kontekst',
    repairedPoneva: 'Rettet af Poneva',
    changeFailed: 'Talerændringen kunne ikke gemmes.',
    editFailed: 'Ændringen i transskriptionen kunne ikke gemmes.',
    splitNeedsWords: 'Linjen har ingen ordtider og kan derfor ikke deles.',
  },
  nl: {
    speakersTitle: 'Sprekers',
    speakerN: 'Spreker {n}',
    talkTime: '{minutes} min · {rows} regels',
    playSamples: 'Fragmenten afspelen',
    pickPerson: 'Wie is dit?',
    addGuest: 'Gast toevoegen',
    notParticipant: 'Geen deelnemer',
    mergeInto: 'Samenvoegen met…',
    unmerge: 'Weer scheiden',
    whoSaidThis: 'Wie zei dit?',
    onlyThisRow: 'Alleen deze regel',
    everyoneLabelled: 'Alles van {label} ({rows} regels)',
    splitHere: 'Hier splitsen',
    whoSaidRest: 'Wie zei de rest?',
    notesRefreshing: 'Sprekers bijgewerkt · notities worden vernieuwd',
    notesUpToDate: 'Notities zijn actueel',
    reaction: 'Reactie van {name}',
    speakersHint:
      'Geef elke stem één keer een naam. Alle regels van die stem volgen en de notities worden vernieuwd.',
    stopSamples: 'Stoppen',
    attendees: 'Deelnemers',
    companyMembers: 'Leden van het bedrijf',
    otherVoices: 'Andere stemmen',
    guestName: 'Naam van de gast',
    addGuestButton: 'Toevoegen',
    suggested: 'Voorstel: {name}',
    mergedHere: '{name} hier samengevoegd',
    mergeWithVoice: 'Zelfde stem als {name}: samenvoegen',
    nameAnyway: 'Toch {name} noemen',
    sameSpeaker: 'Zelfde spreker',
    split: 'Splitsen',
    pickSplitWord: 'Klik op het eerste woord van de volgende spreker.',
    cancel: 'Annuleren',
    edit: 'Bewerken',
    save: 'Opslaan',
    saving: 'Opslaan…',
    transcriptEyebrow: 'Geciteerde bron',
    transcriptTitle: 'Transcriptie',
    viewRepaired: 'Gecorrigeerd',
    viewRaw: 'Ruw',
    transcriptNote:
      'Klik op een sprekersnaam om te corrigeren wie het zei. De ruwe transcriptie blijft altijd bewaard.',
    unlabelled: 'Onbekende spreker',
    gapMarker: 'Onderbreking in de opname · {duration}',
    repairedCompany: 'Gecorrigeerd op basis van bedrijfscontext',
    repairedPoneva: 'Gecorrigeerd door Poneva',
    changeFailed: 'De sprekerwijziging kon niet worden opgeslagen.',
    editFailed: 'De wijziging in de transcriptie kon niet worden opgeslagen.',
    splitNeedsWords: 'Deze regel heeft geen woordtijden en kan daarom niet worden gesplitst.',
  },
  en,
  et: {
    speakersTitle: 'Kõnelejad',
    speakerN: 'Kõneleja {n}',
    talkTime: '{minutes} min · {rows} rida',
    playSamples: 'Esita näiteid',
    pickPerson: 'Kes see on?',
    addGuest: 'Lisa külaline',
    notParticipant: 'Pole osaleja',
    mergeInto: 'Ühenda…',
    unmerge: 'Eralda uuesti',
    whoSaidThis: 'Kes seda ütles?',
    onlyThisRow: 'Ainult see rida',
    everyoneLabelled: 'Kõik märgisega {label} ({rows} rida)',
    splitHere: 'Jaga siit',
    whoSaidRest: 'Kes ütles ülejäänu?',
    notesRefreshing: 'Kõnelejad uuendatud · märkmeid värskendatakse',
    notesUpToDate: 'Märkmed on ajakohased',
    reaction: 'Reaktsioon: {name}',
    speakersHint:
      'Nimeta iga hääl üks kord. Kõik selle hääle read järgivad ja märkmeid värskendatakse.',
    stopSamples: 'Peata',
    attendees: 'Osalejad',
    companyMembers: 'Ettevõtte liikmed',
    otherVoices: 'Teised hääled',
    guestName: 'Külalise nimi',
    addGuestButton: 'Lisa',
    suggested: 'Soovitus: {name}',
    mergedHere: '{name} ühendati siia',
    mergeWithVoice: 'Sama hääl nagu {name}: ühenda',
    nameAnyway: 'Nimeta ikkagi {name}',
    sameSpeaker: 'Sama kõneleja',
    split: 'Jaga',
    pickSplitWord: 'Klõpsa esimesel sõnal, mille järgmine kõneleja ütles.',
    cancel: 'Tühista',
    edit: 'Muuda',
    save: 'Salvesta',
    saving: 'Salvestamine…',
    transcriptEyebrow: 'Viidatud allikas',
    transcriptTitle: 'Transkriptsioon',
    viewRepaired: 'Parandatud',
    viewRaw: 'Toores',
    transcriptNote:
      'Klõpsa kõneleja nimel, et parandada, kes seda ütles. Toores transkriptsioon säilitatakse alati.',
    unlabelled: 'Tundmatu kõneleja',
    gapMarker: 'Salvestuses on lünk · {duration}',
    repairedCompany: 'Parandatud ettevõtte konteksti põhjal',
    repairedPoneva: 'Parandanud Poneva',
    changeFailed: 'Kõneleja muudatust ei õnnestunud salvestada.',
    editFailed: 'Transkriptsiooni muudatust ei õnnestunud salvestada.',
    splitNeedsWords: 'Sellel real pole sõnade aegu, seega ei saa seda jagada.',
  },
  fi: {
    speakersTitle: 'Puhujat',
    speakerN: 'Puhuja {n}',
    talkTime: '{minutes} min · {rows} riviä',
    playSamples: 'Toista näytteet',
    pickPerson: 'Kuka tämä on?',
    addGuest: 'Lisää vieras',
    notParticipant: 'Ei osallistuja',
    mergeInto: 'Yhdistä…',
    unmerge: 'Erota uudelleen',
    whoSaidThis: 'Kuka sanoi tämän?',
    onlyThisRow: 'Vain tämä rivi',
    everyoneLabelled: 'Kaikki merkinnällä {label} ({rows} riviä)',
    splitHere: 'Jaa tästä',
    whoSaidRest: 'Kuka sanoi loput?',
    notesRefreshing: 'Puhujat päivitetty · muistiinpanoja päivitetään',
    notesUpToDate: 'Muistiinpanot ovat ajan tasalla',
    reaction: 'Reaktio: {name}',
    speakersHint:
      'Nimeä kukin ääni kerran. Kaikki sen äänen rivit seuraavat, ja muistiinpanot päivittyvät.',
    stopSamples: 'Pysäytä',
    attendees: 'Osallistujat',
    companyMembers: 'Yrityksen jäsenet',
    otherVoices: 'Muut äänet',
    guestName: 'Vieraan nimi',
    addGuestButton: 'Lisää',
    suggested: 'Ehdotus: {name}',
    mergedHere: '{name} yhdistetty tähän',
    mergeWithVoice: 'Sama ääni kuin {name}: yhdistä',
    nameAnyway: 'Nimeä silti {name}',
    sameSpeaker: 'Sama puhuja',
    split: 'Jaa',
    pickSplitWord: 'Napsauta ensimmäistä sanaa, jonka seuraava puhuja sanoi.',
    cancel: 'Peruuta',
    edit: 'Muokkaa',
    save: 'Tallenna',
    saving: 'Tallennetaan…',
    transcriptEyebrow: 'Viitattu lähde',
    transcriptTitle: 'Litterointi',
    viewRepaired: 'Korjattu',
    viewRaw: 'Raaka',
    transcriptNote:
      'Napsauta puhujan nimeä korjataksesi, kuka sen sanoi. Raaka litterointi säilyy aina.',
    unlabelled: 'Tuntematon puhuja',
    gapMarker: 'Aukko tallenteessa · {duration}',
    repairedCompany: 'Korjattu yrityksen kontekstin perusteella',
    repairedPoneva: 'Ponevan korjaama',
    changeFailed: 'Puhujan muutosta ei voitu tallentaa.',
    editFailed: 'Litteroinnin muokkausta ei voitu tallentaa.',
    splitNeedsWords: 'Rivillä ei ole sanakohtaisia aikoja, joten sitä ei voi jakaa.',
  },
  fr: {
    speakersTitle: 'Intervenants',
    speakerN: 'Intervenant {n}',
    talkTime: '{minutes} min · {rows} lignes',
    playSamples: 'Écouter des extraits',
    pickPerson: 'Qui est-ce ?',
    addGuest: 'Ajouter un invité',
    notParticipant: 'Pas un participant',
    mergeInto: 'Fusionner avec…',
    unmerge: 'Séparer à nouveau',
    whoSaidThis: 'Qui a dit cela ?',
    onlyThisRow: 'Cette ligne uniquement',
    everyoneLabelled: 'Tous les passages de {label} ({rows} lignes)',
    splitHere: 'Couper ici',
    whoSaidRest: 'Qui a dit la suite ?',
    notesRefreshing: 'Intervenants mis à jour · actualisation des notes',
    notesUpToDate: 'Notes à jour',
    reaction: 'Réaction de {name}',
    speakersHint:
      'Nommez chaque voix une seule fois. Toutes les lignes de cette voix suivent et les notes sont actualisées.',
    stopSamples: 'Arrêter',
    attendees: 'Participants',
    companyMembers: 'Membres de l’entreprise',
    otherVoices: 'Autres voix',
    guestName: 'Nom de l’invité',
    addGuestButton: 'Ajouter',
    suggested: 'Suggestion : {name}',
    mergedHere: '{name} fusionné ici',
    mergeWithVoice: 'Même voix que {name} : fusionner',
    nameAnyway: 'L’appeler {name} quand même',
    sameSpeaker: 'Même intervenant',
    split: 'Couper',
    pickSplitWord: 'Cliquez sur le premier mot prononcé par l’intervenant suivant.',
    cancel: 'Annuler',
    edit: 'Modifier',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    transcriptEyebrow: 'Source citée',
    transcriptTitle: 'Transcription',
    viewRepaired: 'Corrigée',
    viewRaw: 'Brute',
    transcriptNote:
      'Cliquez sur le nom d’un intervenant pour corriger qui a parlé. La transcription brute est toujours conservée.',
    unlabelled: 'Intervenant inconnu',
    gapMarker: 'Interruption de l’enregistrement · {duration}',
    repairedCompany: 'Corrigé à partir du contexte de l’entreprise',
    repairedPoneva: 'Corrigé par Poneva',
    changeFailed: 'La modification de l’intervenant n’a pas pu être enregistrée.',
    editFailed: 'La modification de la transcription n’a pas pu être enregistrée.',
    splitNeedsWords: 'Cette ligne n’a pas de minutage des mots et ne peut donc pas être coupée.',
  },
  de: {
    speakersTitle: 'Sprecher',
    speakerN: 'Sprecher {n}',
    talkTime: '{minutes} Min. · {rows} Zeilen',
    playSamples: 'Hörproben abspielen',
    pickPerson: 'Wer ist das?',
    addGuest: 'Gast hinzufügen',
    notParticipant: 'Kein Teilnehmer',
    mergeInto: 'Zusammenführen mit…',
    unmerge: 'Wieder trennen',
    whoSaidThis: 'Wer hat das gesagt?',
    onlyThisRow: 'Nur diese Zeile',
    everyoneLabelled: 'Alle mit {label} ({rows} Zeilen)',
    splitHere: 'Hier teilen',
    whoSaidRest: 'Wer hat den Rest gesagt?',
    notesRefreshing: 'Sprecher aktualisiert · Notizen werden erneuert',
    notesUpToDate: 'Notizen sind aktuell',
    reaction: 'Reaktion von {name}',
    speakersHint:
      'Benennen Sie jede Stimme einmal. Alle Zeilen dieser Stimme folgen, und die Notizen werden erneuert.',
    stopSamples: 'Stopp',
    attendees: 'Teilnehmende',
    companyMembers: 'Mitglieder des Unternehmens',
    otherVoices: 'Andere Stimmen',
    guestName: 'Name des Gastes',
    addGuestButton: 'Hinzufügen',
    suggested: 'Vorschlag: {name}',
    mergedHere: '{name} hier zusammengeführt',
    mergeWithVoice: 'Gleiche Stimme wie {name}: zusammenführen',
    nameAnyway: 'Trotzdem {name} nennen',
    sameSpeaker: 'Gleicher Sprecher',
    split: 'Teilen',
    pickSplitWord: 'Klicken Sie auf das erste Wort des nächsten Sprechers.',
    cancel: 'Abbrechen',
    edit: 'Bearbeiten',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    transcriptEyebrow: 'Zitierte Quelle',
    transcriptTitle: 'Transkript',
    viewRepaired: 'Korrigiert',
    viewRaw: 'Roh',
    transcriptNote:
      'Klicken Sie auf einen Sprechernamen, um zu korrigieren, wer es gesagt hat. Die Rohtranskription bleibt immer erhalten.',
    unlabelled: 'Unbekannter Sprecher',
    gapMarker: 'Aufnahmelücke · {duration}',
    repairedCompany: 'Aus dem Unternehmenskontext korrigiert',
    repairedPoneva: 'Von Poneva korrigiert',
    changeFailed: 'Die Sprecheränderung konnte nicht gespeichert werden.',
    editFailed: 'Die Änderung am Transkript konnte nicht gespeichert werden.',
    splitNeedsWords: 'Diese Zeile hat keine Wortzeiten und kann daher nicht geteilt werden.',
  },
  el: {
    speakersTitle: 'Ομιλητές',
    speakerN: 'Ομιλητής {n}',
    talkTime: '{minutes} λεπ. · {rows} γραμμές',
    playSamples: 'Αναπαραγωγή δειγμάτων',
    pickPerson: 'Ποιος είναι αυτός;',
    addGuest: 'Προσθήκη επισκέπτη',
    notParticipant: 'Δεν είναι συμμετέχων',
    mergeInto: 'Συγχώνευση με…',
    unmerge: 'Διαχωρισμός ξανά',
    whoSaidThis: 'Ποιος το είπε αυτό;',
    onlyThisRow: 'Μόνο αυτή η γραμμή',
    everyoneLabelled: 'Όλα με την ετικέτα {label} ({rows} γραμμές)',
    splitHere: 'Διαίρεση εδώ',
    whoSaidRest: 'Ποιος είπε τα υπόλοιπα;',
    notesRefreshing: 'Οι ομιλητές ενημερώθηκαν · ανανέωση σημειώσεων',
    notesUpToDate: 'Οι σημειώσεις είναι ενημερωμένες',
    reaction: 'Αντίδραση από {name}',
    speakersHint:
      'Δώστε όνομα σε κάθε φωνή μία φορά. Όλες οι γραμμές αυτής της φωνής ακολουθούν και οι σημειώσεις ανανεώνονται.',
    stopSamples: 'Διακοπή',
    attendees: 'Συμμετέχοντες',
    companyMembers: 'Μέλη της εταιρείας',
    otherVoices: 'Άλλες φωνές',
    guestName: 'Όνομα επισκέπτη',
    addGuestButton: 'Προσθήκη',
    suggested: 'Πρόταση: {name}',
    mergedHere: '{name} συγχωνεύτηκε εδώ',
    mergeWithVoice: 'Ίδια φωνή με {name}: συγχώνευση',
    nameAnyway: 'Ονόμασέ τη {name} ούτως ή άλλως',
    sameSpeaker: 'Ίδιος ομιλητής',
    split: 'Διαίρεση',
    pickSplitWord: 'Κάντε κλικ στην πρώτη λέξη που είπε ο επόμενος ομιλητής.',
    cancel: 'Ακύρωση',
    edit: 'Επεξεργασία',
    save: 'Αποθήκευση',
    saving: 'Αποθήκευση…',
    transcriptEyebrow: 'Πηγή αναφοράς',
    transcriptTitle: 'Απομαγνητοφώνηση',
    viewRepaired: 'Διορθωμένη',
    viewRaw: 'Ακατέργαστη',
    transcriptNote:
      'Κάντε κλικ στο όνομα ενός ομιλητή για να διορθώσετε ποιος το είπε. Η ακατέργαστη απομαγνητοφώνηση διατηρείται πάντα.',
    unlabelled: 'Άγνωστος ομιλητής',
    gapMarker: 'Κενό στην εγγραφή · {duration}',
    repairedCompany: 'Διορθώθηκε με βάση το πλαίσιο της εταιρείας',
    repairedPoneva: 'Διορθώθηκε από την Poneva',
    changeFailed: 'Η αλλαγή ομιλητή δεν ήταν δυνατό να αποθηκευτεί.',
    editFailed: 'Η επεξεργασία της απομαγνητοφώνησης δεν ήταν δυνατό να αποθηκευτεί.',
    splitNeedsWords: 'Αυτή η γραμμή δεν έχει χρόνους λέξεων, οπότε δεν μπορεί να διαιρεθεί.',
  },
  hu: {
    speakersTitle: 'Beszélők',
    speakerN: '{n}. beszélő',
    talkTime: '{minutes} perc · {rows} sor',
    playSamples: 'Minták lejátszása',
    pickPerson: 'Ki ez?',
    addGuest: 'Vendég hozzáadása',
    notParticipant: 'Nem résztvevő',
    mergeInto: 'Egyesítés ezzel…',
    unmerge: 'Szétválasztás újra',
    whoSaidThis: 'Ki mondta ezt?',
    onlyThisRow: 'Csak ez a sor',
    everyoneLabelled: 'Minden {label} címkéjű sor ({rows} sor)',
    splitHere: 'Felosztás itt',
    whoSaidRest: 'Ki mondta a többit?',
    notesRefreshing: 'Beszélők frissítve · jegyzetek frissítése',
    notesUpToDate: 'A jegyzetek naprakészek',
    reaction: '{name} reakciója',
    speakersHint:
      'Minden hangot egyszer nevezzen el. A hang összes sora követi, és a jegyzetek frissülnek.',
    stopSamples: 'Leállítás',
    attendees: 'Résztvevők',
    companyMembers: 'A cég tagjai',
    otherVoices: 'Más hangok',
    guestName: 'Vendég neve',
    addGuestButton: 'Hozzáadás',
    suggested: 'Javaslat: {name}',
    mergedHere: '{name} ide egyesítve',
    mergeWithVoice: 'Ugyanaz a hang, mint {name}: egyesítés',
    nameAnyway: 'Mégis legyen {name}',
    sameSpeaker: 'Ugyanaz a beszélő',
    split: 'Felosztás',
    pickSplitWord: 'Kattintson a következő beszélő által mondott első szóra.',
    cancel: 'Mégse',
    edit: 'Szerkesztés',
    save: 'Mentés',
    saving: 'Mentés…',
    transcriptEyebrow: 'Hivatkozott forrás',
    transcriptTitle: 'Átirat',
    viewRepaired: 'Javított',
    viewRaw: 'Nyers',
    transcriptNote:
      'Kattintson egy beszélő nevére, hogy javítsa, ki mondta. A nyers átirat mindig megmarad.',
    unlabelled: 'Ismeretlen beszélő',
    gapMarker: 'Kimaradás a felvételben · {duration}',
    repairedCompany: 'A cég kontextusa alapján javítva',
    repairedPoneva: 'A Poneva javította',
    changeFailed: 'A beszélő módosítását nem sikerült menteni.',
    editFailed: 'Az átirat szerkesztését nem sikerült menteni.',
    splitNeedsWords: 'Ennek a sornak nincsenek szóidőzítései, ezért nem osztható fel.',
  },
  ga: {
    speakersTitle: 'Cainteoirí',
    speakerN: 'Cainteoir {n}',
    talkTime: '{minutes} nóim · {rows} líne',
    playSamples: 'Seinn samplaí',
    pickPerson: 'Cé hé seo?',
    addGuest: 'Cuir aoi leis',
    notParticipant: 'Ní rannpháirtí é',
    mergeInto: 'Cumaisc le…',
    unmerge: 'Scar arís',
    whoSaidThis: 'Cé a dúirt é seo?',
    onlyThisRow: 'An líne seo amháin',
    everyoneLabelled: 'Gach rud faoin lipéad {label} ({rows} líne)',
    splitHere: 'Roinn anseo',
    whoSaidRest: 'Cé a dúirt an chuid eile?',
    notesRefreshing: 'Cainteoirí nuashonraithe · na nótaí á n-athnuachan',
    notesUpToDate: 'Tá na nótaí cothrom le dáta',
    reaction: 'Freagairt ó {name}',
    speakersHint:
      'Tabhair ainm do gach guth uair amháin. Leanfaidh gach líne den ghuth sin, agus athnuafar na nótaí.',
    stopSamples: 'Stad',
    attendees: 'Rannpháirtithe',
    companyMembers: 'Baill den chuideachta',
    otherVoices: 'Guthanna eile',
    guestName: 'Ainm an aoi',
    addGuestButton: 'Cuir leis',
    suggested: 'Moladh: {name}',
    mergedHere: '{name} cumaiscthe anseo',
    mergeWithVoice: 'An guth céanna le {name}: cumaisc',
    nameAnyway: 'Tabhair {name} air mar sin féin',
    sameSpeaker: 'An cainteoir céanna',
    split: 'Roinn',
    pickSplitWord: 'Cliceáil ar an gcéad fhocal a dúirt an chéad chainteoir eile.',
    cancel: 'Cealaigh',
    edit: 'Cuir in eagar',
    save: 'Sábháil',
    saving: 'Á shábháil…',
    transcriptEyebrow: 'Foinse luaite',
    transcriptTitle: 'Tras-scríbhinn',
    viewRepaired: 'Deisithe',
    viewRaw: 'Amh',
    transcriptNote:
      'Cliceáil ar ainm cainteora chun a cheartú cé a dúirt é. Coinnítear an tras-scríbhinn amh i gcónaí.',
    unlabelled: 'Cainteoir anaithnid',
    gapMarker: 'Bearna sa taifeadadh · {duration}',
    repairedCompany: 'Deisithe ó chomhthéacs na cuideachta',
    repairedPoneva: 'Deisithe ag Poneva',
    changeFailed: 'Níorbh fhéidir an t-athrú cainteora a shábháil.',
    editFailed: 'Níorbh fhéidir an eagarthóireacht ar an tras-scríbhinn a shábháil.',
    splitNeedsWords: 'Níl amanna focal ag an líne seo, mar sin ní féidir í a roinnt.',
  },
  it: {
    speakersTitle: 'Interlocutori',
    speakerN: 'Interlocutore {n}',
    talkTime: '{minutes} min · {rows} righe',
    playSamples: 'Ascolta esempi',
    pickPerson: 'Chi è?',
    addGuest: 'Aggiungi un ospite',
    notParticipant: 'Non è un partecipante',
    mergeInto: 'Unisci a…',
    unmerge: 'Separa di nuovo',
    whoSaidThis: 'Chi l’ha detto?',
    onlyThisRow: 'Solo questa riga',
    everyoneLabelled: 'Tutte le righe di {label} ({rows} righe)',
    splitHere: 'Dividi qui',
    whoSaidRest: 'Chi ha detto il resto?',
    notesRefreshing: 'Interlocutori aggiornati · aggiornamento delle note',
    notesUpToDate: 'Note aggiornate',
    reaction: 'Reazione di {name}',
    speakersHint:
      'Assegna un nome a ogni voce una sola volta. Tutte le righe di quella voce si aggiornano e le note vengono rigenerate.',
    stopSamples: 'Ferma',
    attendees: 'Partecipanti',
    companyMembers: 'Membri dell’azienda',
    otherVoices: 'Altre voci',
    guestName: 'Nome dell’ospite',
    addGuestButton: 'Aggiungi',
    suggested: 'Suggerimento: {name}',
    mergedHere: '{name} unito qui',
    mergeWithVoice: 'Stessa voce di {name}: unisci',
    nameAnyway: 'Chiamala comunque {name}',
    sameSpeaker: 'Stesso interlocutore',
    split: 'Dividi',
    pickSplitWord: 'Fai clic sulla prima parola detta dall’interlocutore successivo.',
    cancel: 'Annulla',
    edit: 'Modifica',
    save: 'Salva',
    saving: 'Salvataggio…',
    transcriptEyebrow: 'Fonte citata',
    transcriptTitle: 'Trascrizione',
    viewRepaired: 'Corretta',
    viewRaw: 'Originale',
    transcriptNote:
      'Fai clic sul nome di un interlocutore per correggere chi l’ha detto. La trascrizione originale viene sempre conservata.',
    unlabelled: 'Interlocutore sconosciuto',
    gapMarker: 'Interruzione della registrazione · {duration}',
    repairedCompany: 'Corretto dal contesto aziendale',
    repairedPoneva: 'Corretto da Poneva',
    changeFailed: 'Impossibile salvare la modifica dell’interlocutore.',
    editFailed: 'Impossibile salvare la modifica alla trascrizione.',
    splitNeedsWords: 'Questa riga non ha i tempi delle parole, quindi non può essere divisa.',
  },
  lv: {
    speakersTitle: 'Runātāji',
    speakerN: 'Runātājs {n}',
    talkTime: '{minutes} min · {rows} rindas',
    playSamples: 'Atskaņot paraugus',
    pickPerson: 'Kas tas ir?',
    addGuest: 'Pievienot viesi',
    notParticipant: 'Nav dalībnieks',
    mergeInto: 'Apvienot ar…',
    unmerge: 'Atdalīt atkal',
    whoSaidThis: 'Kas to teica?',
    onlyThisRow: 'Tikai šī rinda',
    everyoneLabelled: 'Visi ar apzīmējumu {label} ({rows} rindas)',
    splitHere: 'Sadalīt šeit',
    whoSaidRest: 'Kas teica pārējo?',
    notesRefreshing: 'Runātāji atjaunināti · piezīmes tiek atsvaidzinātas',
    notesUpToDate: 'Piezīmes ir aktuālas',
    reaction: 'Reakcija no {name}',
    speakersHint:
      'Nosauciet katru balsi vienreiz. Visas šīs balss rindas sekos, un piezīmes tiks atsvaidzinātas.',
    stopSamples: 'Apturēt',
    attendees: 'Dalībnieki',
    companyMembers: 'Uzņēmuma dalībnieki',
    otherVoices: 'Citas balsis',
    guestName: 'Viesa vārds',
    addGuestButton: 'Pievienot',
    suggested: 'Ieteikums: {name}',
    mergedHere: '{name} apvienots šeit',
    mergeWithVoice: 'Tā pati balss kā {name}: apvienot',
    nameAnyway: 'Tomēr nosaukt par {name}',
    sameSpeaker: 'Tas pats runātājs',
    split: 'Sadalīt',
    pickSplitWord: 'Noklikšķiniet uz pirmā vārda, ko teica nākamais runātājs.',
    cancel: 'Atcelt',
    edit: 'Rediģēt',
    save: 'Saglabāt',
    saving: 'Saglabā…',
    transcriptEyebrow: 'Citētais avots',
    transcriptTitle: 'Transkripcija',
    viewRepaired: 'Labots',
    viewRaw: 'Neapstrādāts',
    transcriptNote:
      'Noklikšķiniet uz runātāja vārda, lai labotu, kas to teica. Neapstrādātā transkripcija vienmēr tiek saglabāta.',
    unlabelled: 'Nezināms runātājs',
    gapMarker: 'Pārtraukums ierakstā · {duration}',
    repairedCompany: 'Labots, izmantojot uzņēmuma kontekstu',
    repairedPoneva: 'Laboja Poneva',
    changeFailed: 'Runātāja izmaiņas neizdevās saglabāt.',
    editFailed: 'Transkripcijas labojumu neizdevās saglabāt.',
    splitNeedsWords: 'Šai rindai nav vārdu laika, tāpēc to nevar sadalīt.',
  },
  lt: {
    speakersTitle: 'Kalbėtojai',
    speakerN: 'Kalbėtojas {n}',
    talkTime: '{minutes} min. · {rows} eil.',
    playSamples: 'Leisti pavyzdžius',
    pickPerson: 'Kas tai?',
    addGuest: 'Pridėti svečią',
    notParticipant: 'Ne dalyvis',
    mergeInto: 'Sujungti su…',
    unmerge: 'Vėl atskirti',
    whoSaidThis: 'Kas tai pasakė?',
    onlyThisRow: 'Tik ši eilutė',
    everyoneLabelled: 'Visi pažymėti {label} ({rows} eil.)',
    splitHere: 'Padalyti čia',
    whoSaidRest: 'Kas pasakė likusią dalį?',
    notesRefreshing: 'Kalbėtojai atnaujinti · pastabos atnaujinamos',
    notesUpToDate: 'Pastabos atnaujintos',
    reaction: '{name} reakcija',
    speakersHint:
      'Kiekvieną balsą pavadinkite vieną kartą. Visos to balso eilutės pasikeis, o pastabos bus atnaujintos.',
    stopSamples: 'Sustabdyti',
    attendees: 'Dalyviai',
    companyMembers: 'Įmonės nariai',
    otherVoices: 'Kiti balsai',
    guestName: 'Svečio vardas',
    addGuestButton: 'Pridėti',
    suggested: 'Pasiūlymas: {name}',
    mergedHere: '{name} sujungtas čia',
    mergeWithVoice: 'Tas pats balsas kaip {name}: sujungti',
    nameAnyway: 'Vis tiek pavadinti {name}',
    sameSpeaker: 'Tas pats kalbėtojas',
    split: 'Padalyti',
    pickSplitWord: 'Spustelėkite pirmą žodį, kurį pasakė kitas kalbėtojas.',
    cancel: 'Atšaukti',
    edit: 'Redaguoti',
    save: 'Išsaugoti',
    saving: 'Išsaugoma…',
    transcriptEyebrow: 'Cituojamas šaltinis',
    transcriptTitle: 'Transkripcija',
    viewRepaired: 'Pataisyta',
    viewRaw: 'Neapdorota',
    transcriptNote:
      'Spustelėkite kalbėtojo vardą, kad pataisytumėte, kas tai pasakė. Neapdorota transkripcija visada išsaugoma.',
    unlabelled: 'Nežinomas kalbėtojas',
    gapMarker: 'Įrašo spraga · {duration}',
    repairedCompany: 'Pataisyta pagal įmonės kontekstą',
    repairedPoneva: 'Pataisė Poneva',
    changeFailed: 'Nepavyko išsaugoti kalbėtojo pakeitimo.',
    editFailed: 'Nepavyko išsaugoti transkripcijos pakeitimo.',
    splitNeedsWords: 'Šioje eilutėje nėra žodžių laiko žymų, todėl jos negalima padalyti.',
  },
  mt: {
    speakersTitle: 'Kelliema',
    speakerN: 'Kelliem {n}',
    talkTime: '{minutes} min · {rows} linji',
    playSamples: 'Doqq kampjuni',
    pickPerson: 'Min hu dan?',
    addGuest: 'Żid mistieden',
    notParticipant: 'Mhux parteċipant',
    mergeInto: 'Għaqqad ma’…',
    unmerge: 'Erġa’ ifred',
    whoSaidThis: 'Min qal dan?',
    onlyThisRow: 'Din il-linja biss',
    everyoneLabelled: 'Kulma hu mmarkat {label} ({rows} linji)',
    splitHere: 'Aqsam hawn',
    whoSaidRest: 'Min qal il-bqija?',
    notesRefreshing: 'Kelliema aġġornati · in-noti qed jiġu aġġornati',
    notesUpToDate: 'In-noti huma aġġornati',
    reaction: 'Reazzjoni minn {name}',
    speakersHint:
      'Semmi kull vuċi darba waħda. Il-linji kollha ta’ dik il-vuċi jimxu magħha, u n-noti jiġu aġġornati.',
    stopSamples: 'Waqqaf',
    attendees: 'Parteċipanti',
    companyMembers: 'Membri tal-kumpanija',
    otherVoices: 'Vuċijiet oħra',
    guestName: 'Isem il-mistieden',
    addGuestButton: 'Żid',
    suggested: 'Suġġeriment: {name}',
    mergedHere: '{name} magħqud hawn',
    mergeWithVoice: 'L-istess vuċi bħal {name}: għaqqad',
    nameAnyway: 'Semmiha {name} xorta',
    sameSpeaker: 'L-istess kelliem',
    split: 'Aqsam',
    pickSplitWord: 'Ikklikkja fuq l-ewwel kelma li qal il-kelliem li jmiss.',
    cancel: 'Ikkanċella',
    edit: 'Editja',
    save: 'Issejvja',
    saving: 'Qed jissejvja…',
    transcriptEyebrow: 'Sors ikkwotat',
    transcriptTitle: 'Traskrizzjoni',
    viewRepaired: 'Imtejba',
    viewRaw: 'Oriġinali',
    transcriptNote:
      'Ikklikkja fuq l-isem ta’ kelliem biex tikkoreġi min qalu. It-traskrizzjoni oriġinali tinżamm dejjem.',
    unlabelled: 'Kelliem mhux magħruf',
    gapMarker: 'Vojt fir-reġistrazzjoni · {duration}',
    repairedCompany: 'Ikkoreġut mill-kuntest tal-kumpanija',
    repairedPoneva: 'Ikkoreġut minn Poneva',
    changeFailed: 'Il-bidla fil-kelliem ma setgħetx tissejvja.',
    editFailed: 'L-editjar tat-traskrizzjoni ma setax jissejvja.',
    splitNeedsWords: 'Din il-linja m’għandhiex ħinijiet tal-kliem, għalhekk ma tistax tinqasam.',
  },
  pl: {
    speakersTitle: 'Mówcy',
    speakerN: 'Mówca {n}',
    talkTime: '{minutes} min · {rows} wierszy',
    playSamples: 'Odtwórz próbki',
    pickPerson: 'Kto to jest?',
    addGuest: 'Dodaj gościa',
    notParticipant: 'Nie jest uczestnikiem',
    mergeInto: 'Scal z…',
    unmerge: 'Rozdziel ponownie',
    whoSaidThis: 'Kto to powiedział?',
    onlyThisRow: 'Tylko ten wiersz',
    everyoneLabelled: 'Wszystko oznaczone jako {label} ({rows} wierszy)',
    splitHere: 'Podziel tutaj',
    whoSaidRest: 'Kto powiedział resztę?',
    notesRefreshing: 'Mówcy zaktualizowani · odświeżanie notatek',
    notesUpToDate: 'Notatki są aktualne',
    reaction: 'Reakcja: {name}',
    speakersHint:
      'Nazwij każdy głos raz. Wszystkie wiersze tego głosu się zaktualizują, a notatki zostaną odświeżone.',
    stopSamples: 'Zatrzymaj',
    attendees: 'Uczestnicy',
    companyMembers: 'Członkowie firmy',
    otherVoices: 'Inne głosy',
    guestName: 'Imię gościa',
    addGuestButton: 'Dodaj',
    suggested: 'Sugestia: {name}',
    mergedHere: '{name} scalono tutaj',
    mergeWithVoice: 'Ten sam głos co {name}: scal',
    nameAnyway: 'Mimo to nazwij {name}',
    sameSpeaker: 'Ten sam mówca',
    split: 'Podziel',
    pickSplitWord: 'Kliknij pierwsze słowo, które powiedział następny mówca.',
    cancel: 'Anuluj',
    edit: 'Edytuj',
    save: 'Zapisz',
    saving: 'Zapisywanie…',
    transcriptEyebrow: 'Cytowane źródło',
    transcriptTitle: 'Transkrypcja',
    viewRepaired: 'Poprawiona',
    viewRaw: 'Surowa',
    transcriptNote:
      'Kliknij imię mówcy, aby poprawić, kto to powiedział. Surowa transkrypcja jest zawsze zachowywana.',
    unlabelled: 'Nieznany mówca',
    gapMarker: 'Przerwa w nagraniu · {duration}',
    repairedCompany: 'Poprawione na podstawie kontekstu firmy',
    repairedPoneva: 'Poprawione przez Poneva',
    changeFailed: 'Nie udało się zapisać zmiany mówcy.',
    editFailed: 'Nie udało się zapisać zmiany w transkrypcji.',
    splitNeedsWords: 'Ten wiersz nie ma czasów słów, więc nie można go podzielić.',
  },
  pt: {
    speakersTitle: 'Oradores',
    speakerN: 'Orador {n}',
    talkTime: '{minutes} min · {rows} linhas',
    playSamples: 'Ouvir amostras',
    pickPerson: 'Quem é?',
    addGuest: 'Adicionar um convidado',
    notParticipant: 'Não é participante',
    mergeInto: 'Juntar a…',
    unmerge: 'Separar novamente',
    whoSaidThis: 'Quem disse isto?',
    onlyThisRow: 'Só esta linha',
    everyoneLabelled: 'Todas as linhas de {label} ({rows} linhas)',
    splitHere: 'Dividir aqui',
    whoSaidRest: 'Quem disse o resto?',
    notesRefreshing: 'Oradores atualizados · a atualizar as notas',
    notesUpToDate: 'Notas atualizadas',
    reaction: 'Reação de {name}',
    speakersHint:
      'Dê nome a cada voz uma única vez. Todas as linhas dessa voz acompanham e as notas são atualizadas.',
    stopSamples: 'Parar',
    attendees: 'Participantes',
    companyMembers: 'Membros da empresa',
    otherVoices: 'Outras vozes',
    guestName: 'Nome do convidado',
    addGuestButton: 'Adicionar',
    suggested: 'Sugestão: {name}',
    mergedHere: '{name} juntado aqui',
    mergeWithVoice: 'Mesma voz que {name}: juntar',
    nameAnyway: 'Chamar-lhe {name} mesmo assim',
    sameSpeaker: 'Mesmo orador',
    split: 'Dividir',
    pickSplitWord: 'Clique na primeira palavra dita pelo orador seguinte.',
    cancel: 'Cancelar',
    edit: 'Editar',
    save: 'Guardar',
    saving: 'A guardar…',
    transcriptEyebrow: 'Fonte citada',
    transcriptTitle: 'Transcrição',
    viewRepaired: 'Corrigida',
    viewRaw: 'Original',
    transcriptNote:
      'Clique no nome de um orador para corrigir quem o disse. A transcrição original é sempre guardada.',
    unlabelled: 'Orador desconhecido',
    gapMarker: 'Falha na gravação · {duration}',
    repairedCompany: 'Corrigido com o contexto da empresa',
    repairedPoneva: 'Corrigido pela Poneva',
    changeFailed: 'Não foi possível guardar a alteração do orador.',
    editFailed: 'Não foi possível guardar a edição da transcrição.',
    splitNeedsWords: 'Esta linha não tem tempos por palavra, por isso não pode ser dividida.',
  },
  ro: {
    speakersTitle: 'Vorbitori',
    speakerN: 'Vorbitorul {n}',
    talkTime: '{minutes} min · {rows} rânduri',
    playSamples: 'Redă mostre',
    pickPerson: 'Cine este?',
    addGuest: 'Adaugă un invitat',
    notParticipant: 'Nu este participant',
    mergeInto: 'Unește cu…',
    unmerge: 'Separă din nou',
    whoSaidThis: 'Cine a spus asta?',
    onlyThisRow: 'Doar acest rând',
    everyoneLabelled: 'Tot ce e marcat {label} ({rows} rânduri)',
    splitHere: 'Împarte aici',
    whoSaidRest: 'Cine a spus restul?',
    notesRefreshing: 'Vorbitori actualizați · notițele se reîmprospătează',
    notesUpToDate: 'Notițele sunt actualizate',
    reaction: 'Reacție de la {name}',
    speakersHint:
      'Denumește fiecare voce o singură dată. Toate rândurile acelei voci urmează, iar notițele se reîmprospătează.',
    stopSamples: 'Oprește',
    attendees: 'Participanți',
    companyMembers: 'Membrii companiei',
    otherVoices: 'Alte voci',
    guestName: 'Numele invitatului',
    addGuestButton: 'Adaugă',
    suggested: 'Sugestie: {name}',
    mergedHere: '{name} unit aici',
    mergeWithVoice: 'Aceeași voce ca {name}: unește',
    nameAnyway: 'Numește-o {name} oricum',
    sameSpeaker: 'Același vorbitor',
    split: 'Împarte',
    pickSplitWord: 'Fă clic pe primul cuvânt spus de următorul vorbitor.',
    cancel: 'Anulează',
    edit: 'Editează',
    save: 'Salvează',
    saving: 'Se salvează…',
    transcriptEyebrow: 'Sursă citată',
    transcriptTitle: 'Transcriere',
    viewRepaired: 'Corectată',
    viewRaw: 'Brută',
    transcriptNote:
      'Fă clic pe numele unui vorbitor pentru a corecta cine a spus. Transcrierea brută se păstrează întotdeauna.',
    unlabelled: 'Vorbitor necunoscut',
    gapMarker: 'Întrerupere în înregistrare · {duration}',
    repairedCompany: 'Corectat pe baza contextului companiei',
    repairedPoneva: 'Corectat de Poneva',
    changeFailed: 'Modificarea vorbitorului nu a putut fi salvată.',
    editFailed: 'Editarea transcrierii nu a putut fi salvată.',
    splitNeedsWords: 'Acest rând nu are timpii cuvintelor, deci nu poate fi împărțit.',
  },
  sk: {
    speakersTitle: 'Rečníci',
    speakerN: 'Rečník {n}',
    talkTime: '{minutes} min · {rows} riadkov',
    playSamples: 'Prehrať ukážky',
    pickPerson: 'Kto je to?',
    addGuest: 'Pridať hosťa',
    notParticipant: 'Nie je účastník',
    mergeInto: 'Zlúčiť s…',
    unmerge: 'Znova oddeliť',
    whoSaidThis: 'Kto to povedal?',
    onlyThisRow: 'Iba tento riadok',
    everyoneLabelled: 'Všetko označené {label} ({rows} riadkov)',
    splitHere: 'Rozdeliť tu',
    whoSaidRest: 'Kto povedal zvyšok?',
    notesRefreshing: 'Rečníci aktualizovaní · poznámky sa obnovujú',
    notesUpToDate: 'Poznámky sú aktuálne',
    reaction: 'Reakcia: {name}',
    speakersHint:
      'Pomenujte každý hlas raz. Všetky riadky tohto hlasu sa zmenia a poznámky sa obnovia.',
    stopSamples: 'Zastaviť',
    attendees: 'Účastníci',
    companyMembers: 'Členovia firmy',
    otherVoices: 'Iné hlasy',
    guestName: 'Meno hosťa',
    addGuestButton: 'Pridať',
    suggested: 'Návrh: {name}',
    mergedHere: '{name} zlúčený sem',
    mergeWithVoice: 'Rovnaký hlas ako {name}: zlúčiť',
    nameAnyway: 'Aj tak pomenovať {name}',
    sameSpeaker: 'Ten istý rečník',
    split: 'Rozdeliť',
    pickSplitWord: 'Kliknite na prvé slovo, ktoré povedal ďalší rečník.',
    cancel: 'Zrušiť',
    edit: 'Upraviť',
    save: 'Uložiť',
    saving: 'Ukladá sa…',
    transcriptEyebrow: 'Citovaný zdroj',
    transcriptTitle: 'Prepis',
    viewRepaired: 'Opravený',
    viewRaw: 'Surový',
    transcriptNote:
      'Kliknutím na meno rečníka opravíte, kto to povedal. Surový prepis sa vždy zachová.',
    unlabelled: 'Neznámy rečník',
    gapMarker: 'Medzera v nahrávke · {duration}',
    repairedCompany: 'Opravené podľa kontextu firmy',
    repairedPoneva: 'Opravené službou Poneva',
    changeFailed: 'Zmenu rečníka sa nepodarilo uložiť.',
    editFailed: 'Úpravu prepisu sa nepodarilo uložiť.',
    splitNeedsWords: 'Tento riadok nemá časy slov, preto ho nemožno rozdeliť.',
  },
  sl: {
    speakersTitle: 'Govorci',
    speakerN: 'Govorec {n}',
    talkTime: '{minutes} min · {rows} vrstic',
    playSamples: 'Predvajaj vzorce',
    pickPerson: 'Kdo je to?',
    addGuest: 'Dodaj gosta',
    notParticipant: 'Ni udeleženec',
    mergeInto: 'Združi z…',
    unmerge: 'Znova loči',
    whoSaidThis: 'Kdo je to rekel?',
    onlyThisRow: 'Samo ta vrstica',
    everyoneLabelled: 'Vse z oznako {label} ({rows} vrstic)',
    splitHere: 'Razdeli tukaj',
    whoSaidRest: 'Kdo je rekel preostanek?',
    notesRefreshing: 'Govorci posodobljeni · zapiski se osvežujejo',
    notesUpToDate: 'Zapiski so posodobljeni',
    reaction: 'Odziv: {name}',
    speakersHint:
      'Vsak glas poimenujte enkrat. Vse vrstice tega glasu se posodobijo in zapiski se osvežijo.',
    stopSamples: 'Ustavi',
    attendees: 'Udeleženci',
    companyMembers: 'Člani podjetja',
    otherVoices: 'Drugi glasovi',
    guestName: 'Ime gosta',
    addGuestButton: 'Dodaj',
    suggested: 'Predlog: {name}',
    mergedHere: '{name} združen sem',
    mergeWithVoice: 'Isti glas kot {name}: združi',
    nameAnyway: 'Vseeno poimenuj {name}',
    sameSpeaker: 'Isti govorec',
    split: 'Razdeli',
    pickSplitWord: 'Kliknite prvo besedo, ki jo je rekel naslednji govorec.',
    cancel: 'Prekliči',
    edit: 'Uredi',
    save: 'Shrani',
    saving: 'Shranjevanje…',
    transcriptEyebrow: 'Citirani vir',
    transcriptTitle: 'Prepis',
    viewRepaired: 'Popravljen',
    viewRaw: 'Surov',
    transcriptNote:
      'Kliknite ime govorca, da popravite, kdo je to rekel. Surovi prepis se vedno ohrani.',
    unlabelled: 'Neznan govorec',
    gapMarker: 'Vrzel v posnetku · {duration}',
    repairedCompany: 'Popravljeno na podlagi konteksta podjetja',
    repairedPoneva: 'Popravila storitev Poneva',
    changeFailed: 'Spremembe govorca ni bilo mogoče shraniti.',
    editFailed: 'Popravka prepisa ni bilo mogoče shraniti.',
    splitNeedsWords: 'Ta vrstica nima časov besed, zato je ni mogoče razdeliti.',
  },
  es: {
    speakersTitle: 'Hablantes',
    speakerN: 'Hablante {n}',
    talkTime: '{minutes} min · {rows} filas',
    playSamples: 'Reproducir muestras',
    pickPerson: '¿Quién es?',
    addGuest: 'Añadir un invitado',
    notParticipant: 'No es participante',
    mergeInto: 'Fusionar con…',
    unmerge: 'Separar de nuevo',
    whoSaidThis: '¿Quién dijo esto?',
    onlyThisRow: 'Solo esta fila',
    everyoneLabelled: 'Todo lo marcado como {label} ({rows} filas)',
    splitHere: 'Dividir aquí',
    whoSaidRest: '¿Quién dijo el resto?',
    notesRefreshing: 'Hablantes actualizados · actualizando notas',
    notesUpToDate: 'Notas al día',
    reaction: 'Reacción de {name}',
    speakersHint:
      'Nombra cada voz una sola vez. Todas las filas de esa voz se actualizan y las notas se regeneran.',
    stopSamples: 'Detener',
    attendees: 'Asistentes',
    companyMembers: 'Miembros de la empresa',
    otherVoices: 'Otras voces',
    guestName: 'Nombre del invitado',
    addGuestButton: 'Añadir',
    suggested: 'Sugerencia: {name}',
    mergedHere: '{name} fusionado aquí',
    mergeWithVoice: 'Misma voz que {name}: fusionar',
    nameAnyway: 'Llamarla {name} de todos modos',
    sameSpeaker: 'Mismo hablante',
    split: 'Dividir',
    pickSplitWord: 'Haz clic en la primera palabra que dijo el siguiente hablante.',
    cancel: 'Cancelar',
    edit: 'Editar',
    save: 'Guardar',
    saving: 'Guardando…',
    transcriptEyebrow: 'Fuente citada',
    transcriptTitle: 'Transcripción',
    viewRepaired: 'Corregida',
    viewRaw: 'Original',
    transcriptNote:
      'Haz clic en el nombre de un hablante para corregir quién lo dijo. La transcripción original siempre se conserva.',
    unlabelled: 'Hablante desconocido',
    gapMarker: 'Hueco en la grabación · {duration}',
    repairedCompany: 'Corregido con el contexto de la empresa',
    repairedPoneva: 'Corregido por Poneva',
    changeFailed: 'No se pudo guardar el cambio de hablante.',
    editFailed: 'No se pudo guardar la edición de la transcripción.',
    splitNeedsWords: 'Esta fila no tiene tiempos por palabra, así que no se puede dividir.',
  },
  sv: {
    speakersTitle: 'Talare',
    speakerN: 'Talare {n}',
    talkTime: '{minutes} min · {rows} rader',
    playSamples: 'Spela upp exempel',
    pickPerson: 'Vem är detta?',
    addGuest: 'Lägg till en gäst',
    notParticipant: 'Inte en deltagare',
    mergeInto: 'Slå ihop med…',
    unmerge: 'Dela upp igen',
    whoSaidThis: 'Vem sa detta?',
    onlyThisRow: 'Bara den här raden',
    everyoneLabelled: 'Alla märkta {label} ({rows} rader)',
    splitHere: 'Dela här',
    whoSaidRest: 'Vem sa resten?',
    notesRefreshing: 'Talare uppdaterade · anteckningarna uppdateras',
    notesUpToDate: 'Anteckningarna är aktuella',
    reaction: 'Reaktion från {name}',
    speakersHint:
      'Namnge varje röst en gång. Alla rader med den rösten följer med, och anteckningarna uppdateras.',
    stopSamples: 'Stoppa',
    attendees: 'Deltagare',
    companyMembers: 'Medlemmar i företaget',
    otherVoices: 'Andra röster',
    guestName: 'Gästens namn',
    addGuestButton: 'Lägg till',
    suggested: 'Förslag: {name}',
    mergedHere: '{name} ihopslagen hit',
    mergeWithVoice: 'Samma röst som {name}: slå ihop',
    nameAnyway: 'Kalla den {name} ändå',
    sameSpeaker: 'Samma talare',
    split: 'Dela',
    pickSplitWord: 'Klicka på det första ordet som nästa talare sa.',
    cancel: 'Avbryt',
    edit: 'Redigera',
    save: 'Spara',
    saving: 'Sparar…',
    transcriptEyebrow: 'Citerad källa',
    transcriptTitle: 'Transkription',
    viewRepaired: 'Rättad',
    viewRaw: 'Rå',
    transcriptNote:
      'Klicka på ett talarnamn för att rätta vem som sa det. Den råa transkriptionen sparas alltid.',
    unlabelled: 'Okänd talare',
    gapMarker: 'Lucka i inspelningen · {duration}',
    repairedCompany: 'Rättad utifrån företagets kontext',
    repairedPoneva: 'Rättad av Poneva',
    changeFailed: 'Talarändringen kunde inte sparas.',
    editFailed: 'Ändringen i transkriptionen kunde inte sparas.',
    splitNeedsWords: 'Raden saknar ordtider och kan därför inte delas.',
  },
};
export default meetingsSpeakersCopy;
